import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../SafeIcon';
import DiscoverSection from './DiscoverSection';

const { FiArrowRight, FiCheck, FiMenu, FiX, FiZap, FiShoppingBag, FiLayout, FiBarChart2, FiGlobe, FiStar, FiChevronDown } = FiIcons;

const NAV_LINKS = ['Features', 'Pricing', 'Solutions', 'Resources'];

const FEATURES = [
  { icon: FiLayout, title: 'Drag & Drop Builder', desc: 'Build stunning pages visually with zero code. Arrange sections, widgets, and content in real time.' },
  { icon: FiShoppingBag, title: 'Built-in Store', desc: 'Launch your e-commerce store instantly. Manage products, orders, and payments from one dashboard.' },
  { icon: FiBarChart2, title: 'Analytics & Insights', desc: 'Track conversions, visitor behavior, and revenue with a powerful real-time dashboard.' },
  { icon: FiGlobe, title: 'Custom Domains', desc: 'Connect your own domain and go live in minutes. Full DNS management included.' },
  { icon: FiZap, title: 'Lightning Fast', desc: 'Every site is optimized for speed, SEO, and mobile — out of the box, no configuration needed.' },
  { icon: FiStar, title: 'Multi-Page Sites', desc: 'Build multi-page websites and funnels. Manage all your projects from a single workspace.' },
];

const PLANS = [
  { name: 'Starter', price: 'R0', period: '/mo', desc: 'Perfect for getting started', cta: 'Start for free', highlight: false, features: ['1 website', '5 pages', 'Drag & drop builder', 'Basic analytics', 'Community support'] },
  { name: 'Growth', price: 'R299', period: '/mo', desc: 'For growing businesses', cta: 'Start free trial', highlight: true, features: ['Unlimited websites', 'Unlimited pages', 'E-commerce store', 'Custom domain', 'Payment integrations', 'Priority support'] },
  { name: 'Pro', price: 'R799', period: '/mo', desc: 'For scaling teams', cta: 'Start free trial', highlight: false, features: ['Everything in Growth', 'Team collaboration', 'Advanced analytics', 'White-label option', 'API access', 'Dedicated support'] },
];

const TESTIMONIALS = [
  { name: 'Sarah M.', role: 'Boutique Owner', quote: 'I built my entire online store in a single afternoon. The drag-and-drop is insanely intuitive.', avatar: 'SM' },
  { name: 'James K.', role: 'Freelance Designer', quote: 'My clients love the results. I can deliver beautiful sites 3x faster than before.', avatar: 'JK' },
  { name: 'Priya N.', role: 'Marketing Lead', quote: 'The analytics and multi-page funnels have transformed how we run campaigns.', avatar: 'PN' },
];

const FAQ_ITEMS = [
  { q: 'Do I need coding skills?', a: 'Not at all. Our drag-and-drop builder is designed for everyone — no code required.' },
  { q: 'Can I use my own domain?', a: 'Yes! Connect any custom domain through our simple DNS management panel.' },
  { q: 'How does the store work?', a: 'Add products, set prices, connect a payment provider like PayFast, and start selling within minutes.' },
  { q: 'Is there a free plan?', a: 'Yes. The Starter plan is completely free with no credit card required.' },
];

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left">
        <span className="font-semibold text-gray-800 text-base">{item.q}</span>
        <SafeIcon icon={FiChevronDown} className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <p className="pb-5 text-gray-500 text-sm leading-relaxed">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const MARQUEE_ITEMS = ['Websites', 'Online Stores', 'Funnels', 'Landing Pages', 'Blogs', 'Portfolios', 'Bookings', 'Subscriptions'];

// ── Cinematic animated background ──────────────────────────────────────────
function CinematicBackground() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const targetMouseRef = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;
    canvas.width = width;
    canvas.height = height;

    const handleResize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      targetMouseRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      };
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);

    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      size: Math.random() * 1.5 + 0.3,
      opacity: Math.random() * 0.5 + 0.1,
      hue: Math.random() > 0.7 ? 80 : 0,
    }));

    const orbs = [
      { x: 0.2, y: 0.3, r: 0.35, color: '#c8f542', speed: 0.0003 },
      { x: 0.8, y: 0.7, r: 0.3, color: '#7c3aed', speed: 0.0004 },
      { x: 0.5, y: 0.9, r: 0.25, color: '#0ea5e9', speed: 0.00035 },
    ];

    let t = 0;

    const draw = () => {
      t += 1;
      mouseRef.current.x += (targetMouseRef.current.x - mouseRef.current.x) * 0.04;
      mouseRef.current.y += (targetMouseRef.current.y - mouseRef.current.y) * 0.04;
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#060608';
      ctx.fillRect(0, 0, width, height);

      orbs.forEach((orb, i) => {
        const parallaxX = (mouseRef.current.x - 0.5) * 60 * (i % 2 === 0 ? 1 : -1);
        const parallaxY = (mouseRef.current.y - 0.5) * 40 * (i % 2 === 0 ? 1 : -1);
        const cx = (orb.x + Math.sin(t * orb.speed * 1000 + i) * 0.12) * width + parallaxX;
        const cy = (orb.y + Math.cos(t * orb.speed * 1000 + i * 1.5) * 0.08) * height + parallaxY;
        const r = orb.r * Math.min(width, height);
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, orb.color + '18');
        grad.addColorStop(0.4, orb.color + '08');
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      });

      const mx = mouseRef.current.x * width;
      const my = mouseRef.current.y * height;
      const mouseGrad = ctx.createRadialGradient(mx, my, 0, mx, my, 280);
      mouseGrad.addColorStop(0, '#c8f54210');
      mouseGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = mouseGrad;
      ctx.fillRect(0, 0, width, height);

      ctx.save();
      for (let y = 0; y < height; y += 4) {
        ctx.fillStyle = 'rgba(0,0,0,0.08)';
        ctx.fillRect(0, y, width, 2);
      }
      ctx.restore();

      ctx.save();
      ctx.strokeStyle = 'rgba(200,245,66,0.03)';
      ctx.lineWidth = 1;
      const gridSize = 60;
      const offsetX = (t * 0.15) % gridSize;
      const offsetY = (t * 0.08) % gridSize;
      for (let x = -gridSize + offsetX; x < width + gridSize; x += gridSize) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
      }
      for (let y = -gridSize + offsetY; y < height + gridSize; y += gridSize) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
      }
      ctx.restore();

      particles.forEach((p) => {
        const dx = mx - p.x;
        const dy = my - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 200) {
          p.vx += (dx / dist) * 0.005;
          p.vy += (dy / dist) * 0.005;
        }
        p.vx *= 0.98;
        p.vy *= 0.98;
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;
        const pulse = 0.5 + 0.5 * Math.sin(t * 0.02 + p.x * 0.01);
        ctx.save();
        ctx.globalAlpha = p.opacity * pulse;
        ctx.fillStyle = p.hue === 80 ? '#c8f542' : '#ffffff';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      const vignette = ctx.createRadialGradient(width / 2, height / 2, height * 0.2, width / 2, height / 2, height * 0.9);
      vignette.addColorStop(0, 'transparent');
      vignette.addColorStop(1, 'rgba(0,0,0,0.7)');
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, width, height);

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ display: 'block' }}
    />
  );
}

// Smooth scroll helper
function scrollToDiscover() {
  const el = document.getElementById('discover-sidehustle');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export default function LandingPage({ onEnterApp }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">
      {/* NAV */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-md shadow-lg shadow-black/20' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center space-x-2">
            <div className="w-7 h-7 bg-[#c8f542] rounded-md flex items-center justify-center">
              <span className="text-black font-black text-xs">S</span>
            </div>
            <span className="text-xl font-black text-white tracking-tight">SideHustlr</span>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            {NAV_LINKS.map((l) => <a key={l} href="#" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">{l}</a>)}
          </nav>
          <div className="hidden md:flex items-center space-x-3">
            <button onClick={onEnterApp} className="text-sm font-semibold text-gray-300 hover:text-white transition-colors px-3 py-1.5">Log in</button>
            <button
              onClick={onEnterApp}
              className="relative bg-[#c8f542] text-black text-sm font-black px-5 py-2.5 rounded-full transition-all duration-300 hover:bg-[#d4ff50] hover:shadow-[0_0_24px_6px_rgba(200,245,66,0.5)] hover:scale-105"
            >
              Start for free
            </button>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-gray-300">
            <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="md:hidden bg-[#0a0a0a] border-t border-white/10 px-6 py-4 space-y-3">
              {NAV_LINKS.map((l) => <a key={l} href="#" className="block text-sm font-medium text-gray-300 py-2">{l}</a>)}
              <button onClick={onEnterApp} className="w-full bg-[#c8f542] text-black text-sm font-black px-5 py-3 rounded-full mt-2 hover:shadow-[0_0_20px_4px_rgba(200,245,66,0.4)] transition-all">Start for free</button>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        <CinematicBackground />

        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
            backgroundSize: '128px 128px',
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-24 pb-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center space-x-2 border border-white/10 bg-white/5 backdrop-blur-sm text-gray-300 text-xs font-bold px-4 py-2 rounded-full mb-8 uppercase tracking-widest"
            >
              <div className="w-2 h-2 bg-[#c8f542] rounded-full animate-pulse" />
              <span>The all-in-one platform for side hustlers</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-6xl md:text-8xl font-black text-white leading-[0.95] tracking-tight mb-6"
            >
              Build your<br />
              <span className="text-[#c8f542] relative">
                side hustle
                <motion.span
                  className="absolute inset-0 blur-2xl opacity-30 bg-[#c8f542]"
                  animate={{ opacity: [0.2, 0.5, 0.2] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
              </span>
              <br />
              <span className="text-white">online.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed font-light"
            >
              Create stunning websites, launch your store, and start earning — all from one powerful platform built for entrepreneurs.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12"
            >
              {/* PRIMARY CTA */}
              <button
                onClick={onEnterApp}
                className="w-full sm:w-auto group relative bg-[#c8f542] text-black text-base font-black px-10 py-4 rounded-full transition-all duration-300 hover:bg-[#d4ff50] hover:scale-105 hover:shadow-[0_0_40px_12px_rgba(200,245,66,0.45)] flex items-center justify-center space-x-2 overflow-hidden"
              >
                <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 pointer-events-none" />
                <span className="relative z-10">Start building free</span>
                <SafeIcon icon={FiArrowRight} className="relative z-10 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>

              {/* SECONDARY CTA — now scrolls to discover section */}
              <button
                onClick={scrollToDiscover}
                className="w-full sm:w-auto group bg-white/5 border border-white/10 text-white text-base font-bold px-10 py-4 rounded-full hover:bg-white/10 hover:border-[#c8f542]/30 hover:shadow-[0_0_20px_4px_rgba(200,245,66,0.15)] transition-all backdrop-blur-sm flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiZap} className="w-4 h-4 text-[#c8f542]" />
                <span>Discover my Sidehustle</span>
              </button>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-xs text-gray-500 font-medium"
            >
              Free forever · No credit card required · Cancel anytime
            </motion.p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="flex flex-wrap items-center justify-center gap-8 mt-16 pt-8 border-t border-white/5"
          >
            {[{ num: '10K+', label: 'Businesses built' }, { num: 'R2M+', label: 'Revenue generated' }, { num: '4.9★', label: 'Average rating' }, { num: '99.9%', label: 'Uptime' }].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-black text-white">{s.num}</div>
                <div className="text-xs text-gray-500 mt-1 uppercase tracking-widest">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Browser mockup */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="relative z-10 w-full max-w-5xl mx-auto px-6 pb-0"
        >
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">
            <div className="bg-[#1a1a1a] px-5 py-3 flex items-center space-x-2 border-b border-white/10">
              <div className="w-3 h-3 rounded-full bg-red-500/60" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
              <div className="w-3 h-3 rounded-full bg-green-500/60" />
              <div className="flex-1 mx-4 bg-white/5 rounded-md px-4 py-1.5 text-xs text-gray-500 font-mono">mysidehustlr.co.za</div>
              <div className="w-2 h-2 rounded-full bg-[#c8f542]" />
            </div>
            <div className="bg-[#111111] p-8 min-h-[280px]">
              <div className="flex items-center justify-between mb-8">
                <div className="h-4 w-24 bg-white/10 rounded-full" />
                <div className="flex space-x-3">
                  <div className="h-3 w-12 bg-white/5 rounded-full" />
                  <div className="h-3 w-12 bg-white/5 rounded-full" />
                  <div className="h-8 w-20 bg-[#c8f542]/80 rounded-full" />
                </div>
              </div>
              <div className="text-center space-y-4 pt-4">
                <div className="h-10 bg-white/10 rounded-xl w-3/5 mx-auto" />
                <div className="h-10 bg-white/20 rounded-xl w-4/5 mx-auto" />
                <div className="flex justify-center space-x-3 pt-4">
                  <div className="h-12 w-36 bg-[#c8f542]/80 rounded-full" />
                  <div className="h-12 w-36 bg-white/5 rounded-full border border-white/10" />
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-5 bg-[#c8f542] overflow-hidden">
        <div className="flex whitespace-nowrap">
          <div className="flex items-center space-x-8 px-4" style={{ animation: 'marquee 20s linear infinite' }}>
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <span key={i} className="text-black font-black text-sm uppercase tracking-widest flex items-center space-x-4">
                <span>{item}</span><span className="text-black/30">✦</span>
              </span>
            ))}
          </div>
        </div>
        <style>{`@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
      </section>

      {/* ── DISCOVER MY SIDEHUSTLE SECTION ── */}
      <DiscoverSection onEnterApp={onEnterApp} />

      {/* FEATURES */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-black uppercase tracking-widest text-[#c8f542] bg-black px-3 py-1 rounded-full">Features</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mt-4 mb-4">Everything you need to succeed</h2>
            <p className="text-lg text-gray-500 max-w-xl mx-auto">One platform. Every tool. Zero complexity.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="p-8 rounded-2xl border border-gray-100 hover:border-black hover:shadow-xl transition-all group bg-white">
                <div className="w-11 h-11 bg-black rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#c8f542] transition-colors">
                  <SafeIcon icon={f.icon} className="w-5 h-5 text-white group-hover:text-black transition-colors" />
                </div>
                <h3 className="font-black text-gray-900 text-lg mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-black uppercase tracking-widest text-[#c8f542] bg-black px-3 py-1 rounded-full">Pricing</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mt-4 mb-4">Simple, honest pricing</h2>
            <p className="text-gray-500 text-lg">Start free. Upgrade when you're ready to grow.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map((plan, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className={`rounded-2xl p-8 flex flex-col ${plan.highlight ? 'bg-[#0a0a0a] text-white ring-2 ring-[#c8f542] shadow-2xl scale-105' : 'bg-gray-50 border border-gray-200'}`}>
                {plan.highlight && <span className="text-xs font-black uppercase tracking-widest text-[#c8f542] mb-3">Most Popular</span>}
                <h3 className={`text-xl font-black mb-1 ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                <p className={`text-sm mb-6 ${plan.highlight ? 'text-gray-400' : 'text-gray-500'}`}>{plan.desc}</p>
                <div className="flex items-end space-x-1 mb-8">
                  <span className={`text-4xl font-black ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>{plan.price}</span>
                  <span className="text-sm mb-1 text-gray-400">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-center space-x-2 text-sm">
                      <SafeIcon icon={FiCheck} className={`w-4 h-4 shrink-0 ${plan.highlight ? 'text-[#c8f542]' : 'text-black'}`} />
                      <span className={plan.highlight ? 'text-gray-300' : 'text-gray-600'}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onEnterApp}
                  className={`w-full py-3.5 rounded-full font-black text-sm transition-all duration-300 ${plan.highlight ? 'bg-[#c8f542] hover:bg-[#d4ff50] text-black hover:shadow-[0_0_28px_8px_rgba(200,245,66,0.4)] hover:scale-105' : 'bg-black text-white hover:bg-gray-800'}`}
                >
                  {plan.cta}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-black uppercase tracking-widest text-[#c8f542] bg-black px-3 py-1 rounded-full">Reviews</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mt-4">Loved by builders everywhere</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-all">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, j) => <SafeIcon key={j} icon={FiStar} className="w-4 h-4 text-yellow-400" style={{ fill: 'currentColor' }} />)}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center text-xs font-black">{t.avatar}</div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl font-black text-gray-900 text-center mb-12">Frequently asked questions</h2>
          <div className="divide-y divide-gray-100 border border-gray-100 rounded-2xl px-8 py-2">
            {FAQ_ITEMS.map((item, i) => <FaqItem key={i} item={item} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(#ffffff 1px,transparent 1px),linear-gradient(90deg,#ffffff 1px,transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
            Your side hustle<br /><span className="text-[#c8f542]">starts here.</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10">Join thousands of entrepreneurs who build and sell with SideHustlr.</p>
          <button
            onClick={onEnterApp}
            className="group relative bg-[#c8f542] hover:bg-[#d4ff50] text-black font-black text-lg px-14 py-5 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-[0_0_50px_15px_rgba(200,245,66,0.4)] inline-flex items-center space-x-3 overflow-hidden"
          >
            <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 pointer-events-none" />
            <span className="relative z-10">Start building — it's free</span>
            <SafeIcon icon={FiArrowRight} className="relative z-10 w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-gray-600 text-sm mt-4">No credit card required · Cancel anytime</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#050505] text-gray-500 py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-[#c8f542] rounded-md flex items-center justify-center">
              <span className="text-black font-black text-xs">S</span>
            </div>
            <span className="text-lg font-black text-white tracking-tight">SideHustlr</span>
          </div>
          <div className="flex items-center space-x-6 text-sm">
            {['Privacy', 'Terms', 'Contact'].map((l) => <a key={l} href="#" className="hover:text-white transition-colors">{l}</a>)}
          </div>
          <p className="text-xs text-gray-700">© 2025 SideHustlr. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}